import { Sheet, SheetTitle, SheetHeader, SheetContent, SheetTrigger, SheetFooter } from "@/shared/ui/sheet.tsx";
import { Button } from "../ui/button.tsx";
import ImageSection from "./ImageSection";
import PostBodyForm from "./PostBodyForm";
import { useState } from "react";
// import { useGetSocialAttributes } from "./api/attributes-api";
// import AttributesForm from "./AttributesForm";
import { PostBodyFormSchema, PostBodyFormType, SocialPostType } from "./types";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useGetCatalog, useRegeneratePostImage, useSchedulePost, useUpdatePostBody } from "./api/single-post-api";
import { format } from "date-fns";
import {
  Save, SignpostBig
} from "lucide-react";
import { useAtomValue } from "jotai";
import { themeAtom } from "@/features/app-layout/atoms/theme-atoms";


type EditPostSheetProps = {
  location: "scheduled" | "review" | "discarded"
  variant: "scheduled" | "pending"
  trigger: JSX.Element,
  postDetails: SocialPostType['content'][number],
  eventDetails: {
    event_id: number,
    event_name: string,
  },
  eventIndex?: number,
  onSchedule: (id: number) => void
}

const EditPostSheet = ({ eventDetails, postDetails, trigger, onSchedule, variant, location }: EditPostSheetProps) => {

  const theme = useAtomValue(themeAtom)

  const [open, setOpen] = useState(false)
  const [isImageLoading, setIsImageLoading] = useState(false)

  const { schedulePost } = useSchedulePost(location, onSchedule)

  const { catalogData } = useGetCatalog(open)

  const setImageLoader = () => {
    setIsImageLoading(false)
  }
  const { regenerateImage, data } = useRegeneratePostImage(setImageLoader)

  const { updatePostBody, isSuccess } = useUpdatePostBody()

  const form = useForm<PostBodyFormType>({
    resolver: zodResolver(PostBodyFormSchema),
    defaultValues: {
      post_id: postDetails.post_id,
      post_content: postDetails.post_content,
      hashtags: postDetails.tags,
      post_date: new Date(postDetails.post_date),
      post_time: postDetails.post_time,
      image_recommendation: postDetails.image_video_recommendation,
      catalogue: postDetails.catalogue
    }
  })

  const savePostHandler = () => {
    form.handleSubmit(submitHandler, errorHandler)()
  }


  const submitHandler = (data: PostBodyFormType) => {
    console.log(data)
    updatePostBody({ ...data, post_date: format(data.post_date, "yyyy-MM-dd") })
  }
  const errorHandler = (errors: FieldErrors<PostBodyFormType>) => {
    console.error("Form errors:", errors);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger}
      </SheetTrigger>
      <SheetContent className="w-[100vw] sm:w-[80vw] md:w-[75vw] h-[100vh] flex flex-col justify-between overflow-auto bg-gray-50">
        <SheetHeader>
          <SheetTitle className="text-xl font-montserrat font-medium">Edit post for {eventDetails.event_name}</SheetTitle>
        </SheetHeader>
        <div className="w-full flex flex-col md:flex-row gap-2 h-[90vh] overflow-auto">
          <ImageSection
            isSuccess={isSuccess}
            onSavePost={savePostHandler}
            post_id={postDetails.post_id}
            isLoading={isImageLoading}
            setIsLoading={setIsImageLoading}
            generateImage={regenerateImage}
            postForm={form} 
            catalog={catalogData ? catalogData.catalogue : []}
            imgUrl={data ? data.data.image_url : postDetails.image_url}
            image_recommendation={postDetails.image_video_recommendation} />
          <div className="relative w-full md:w-1/2">
            <PostBodyForm postForm={form} catalog={catalogData ? catalogData.catalogue : []} />
          </div>
        </div>
        <SheetFooter className="flex flex-row flex-wrap items-center gap-2 w-full">
          <Button type="button" style={{ backgroundColor:  theme.primary}} className="flex items-center gap-2 max-sm:text-xs"
            onClick={savePostHandler}><Save /> Save changes</Button>
          {variant === "pending" && (
            <Button type="button" style={{ backgroundColor:  theme.primary}} className="max-sm:text-xs flex items-center gap-2" onClick={() => {
              schedulePost(postDetails.post_id)
              setOpen(false)
            }}><SignpostBig /> Schedule Post</Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

export default EditPostSheet;
